import React, { useState, useEffect, useMemo } from 'react';
import { 
    FileText, RefreshCcw, Archive, Download, Calendar, 
    FileDown, ListFilter, Eye, Trash2, Lock, Snowflake, Inbox, Leaf,
    AlertTriangle, X, CheckCircle, Info
} from 'lucide-react';
import API from '../../services/api';
import Sidebar from '../../components/Sidebar';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// --- COMPOSANT MODAL DE CONFIRMATION ET ALERTE ---
const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmColor = "#dc2626", isAlert = false }) => {
    if (!isOpen) return null;
    return (
        <div style={modalOverlay}>
            <div style={modalContent}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: confirmColor }}>
                        {isAlert ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '900' }}>{title}</h3>
                    </div>
                    <button onClick={onCancel} style={btnCloseModal}><X size={18} /></button>
                </div>
                <p style={{ fontSize: '14px', color: '#475569', marginBottom: '20px', fontWeight: '500' }}>{message}</p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    {!isAlert && (
                        <button onClick={onCancel} style={btnCancelModal}>Annuler</button>
                    )}
                    <button 
                        onClick={isAlert ? onCancel : onConfirm} 
                        style={{ ...btnConfirmModal, background: confirmColor }}
                    >
                        {isAlert ? "Fermer" : "Confirmer"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const HistoriqueMp = () => {
    // --- ÉTATS ---
    const [achats, setAchats] = useState([]);
    const [loading, setLoading] = useState(true);
const [showFullHistory, setShowFullHistory] = useState(true);
    const [isInventoryActive, setIsInventoryActive] = useState(false);
    const [lastClosureDate, setLastClosureDate] = useState(null);
    const [activeTab, setActiveTab] = useState('actif'); 

    // --- ÉTATS MODAL (Gère confirmation ET alertes de succès/erreur) ---
    const [modalConfig, setModalConfig] = useState({ 
        isOpen: false, 
        targetId: null, 
        type: null, 
        title: '', 
        message: '', 
        isAlert: false,
        confirmColor: '#dc2626'
    });

    const initialDates = { start: '', end: '' };
    const initialFilters = {
        id: '', lot_id: '', num_facture: '', matiere_id: '', nom_matiere_snap: '', 
        nom_fournisseur_snap: '', statut: 'tous', nom_utilisateur: '', date_achat: ''
    };

    const [dateRange, setDateRange] = useState(initialDates);
    const [colFilters, setColFilters] = useState(initialFilters);

    const fetchData = async () => {
        setLoading(true);
        try {
            const resPurchases = await API.get('/achats-mp'); 
            setAchats(Array.isArray(resPurchases.data) ? resPurchases.data : []);

            try {
                const resInvStatus = await API.get('/inventories/check-status'); 
                const isPending = !!resInvStatus.data.en_cours;
                setIsInventoryActive(isPending); 
                setLastClosureDate(resInvStatus.data.last_closure || null);
            } catch (invErr) {
                setIsInventoryActive(false);
            }
        } catch (err) {
            console.error("Erreur de chargement des achats MP", err);
        } finally {
            setLoading(false);
        }
    };

   useEffect(() => {
    fetchData(); 

    const handleGlobalUpdate = (event) => {
        const { table } = event.detail;
        
        // 🔥 CORRECTION : On doit écouter 'purchases_mp' pour ce module !
        // 'production_needs' c'est pour le module de production.
        if (table === 'purchases_mp' || table === 'all') {
            console.log("🔄 Signal reçu : Mise à jour de l'historique MP...");
            fetchData();
        }
    };

    window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
}, []);

    const isRowFrozen = (dateAchat) => {
        if (!lastClosureDate || !dateAchat) return false;
        const tempsAchat = new Date(dateAchat).getTime();
        const tempsCloture = new Date(lastClosureDate).getTime();
        return tempsAchat <= tempsCloture;
    };

    // --- ACTIONS ---
    const openConfirm = (targetId, type) => {
        let config = { isOpen: true, targetId, type, isAlert: false };
        
        switch(type) {
            case 'DELETE':
                config.title = "SUPPRESSION DU LOT COMPLET";
                config.message = "ATTENTION : Vous allez supprimer TOUTES les lignes de ce lot. Le stock et le CMP global seront impactés.";
                config.confirmColor = "#dc2626";
                break;
            case 'DELETE_SINGLE':
                config.title = "SUPPRESSION D'UNE LIGNE";
                config.message = "Voulez-vous supprimer uniquement cette ligne d'achat ? Le stock de cet article et son CMP seront recalculés.";
                config.confirmColor = "#dc2626";
                break;
            case 'ARCHIVE':
                config.title = "ARCHIVAGE DU LOT";
                config.message = "Archiver ce lot ? Cela fige les données pour l'inventaire.";
                config.confirmColor = "#166534";
                break;
            case 'UNARCHIVE':
                config.title = "DÉSARCHIVAGE DU LOT";
                config.message = "Remettre ce lot en état actif ?";
                config.confirmColor = "#166534";
                break;
            default: break;
        }
        setModalConfig(config);
    };

    const showAlert = (title, message, color = "#166534") => {
        setModalConfig({
            isOpen: true,
            title,
            message,
            isAlert: true,
            confirmColor: color,
            targetId: null,
            type: null
        });
    };

    const handleConfirmAction = async () => {
        const { targetId, type } = modalConfig;
        setLoading(true);
        
        try {
            let response;
            if (type === 'DELETE') {
                response = await API.put(`/achats-mp/annuler/${targetId}`);
            } else if (type === 'DELETE_SINGLE') {
                response = await API.delete(`/raw-materials/ligne/${targetId}`); 
            } else if (type === 'ARCHIVE') {
                response = await API.put(`/achats-mp/archiver/${targetId}`);
            } else if (type === 'UNARCHIVE') {
                response = await API.put(`/achats-mp/desarchiver/${targetId}`);
            }

            await fetchData();
            setModalConfig({ isOpen: false }); // Ferme d'abord la confirmation
            
            // Affiche le succès via la modale en mode alerte
            setTimeout(() => {
                showAlert("SUCCÈS", "L'opération a été effectuée avec succès.", "#166534");
            }, 100);

        } catch (err) {
            const errorMsg = err.response?.data?.error || "Une erreur est survenue lors de l'opération.";
            setModalConfig({ isOpen: false });
            setTimeout(() => {
                showAlert("ERREUR", errorMsg, "#dc2626");
            }, 100);
        } finally {
            setLoading(false);
        }
    };

    // --- LOGIQUE DE CALCULS ---
    const counts = useMemo(() => {
        const totals = { actif: 0, archives: 0, supprimes: 0 };
        achats.forEach(a => {
            if (a.is_active === 0) totals.supprimes++;
            else if (a.is_archived === 1) totals.archives++;
            else totals.actif++;
        });
        return totals;
    }, [achats]);

    const achatsFiltresParOnglet = useMemo(() => {
        return achats.filter(a => {
            if (activeTab === 'supprimes') return a.is_active === 0;
            if (activeTab === 'archives') return a.is_active === 1 && a.is_archived === 1;
            return a.is_active === 1 && a.is_archived === 0;
        });
    }, [achats, activeTab]);

 const filteredAchatsDetail = useMemo(() => {
    // 1. On crée d'abord une liste "plate" de toutes les lignes d'articles
    const toutesLesLignes = [];
    achatsFiltresParOnglet.forEach(achat => {
        if (achat.items && Array.isArray(achat.items)) {
            achat.items.forEach(item => {
                toutesLesLignes.push({
                    ...achat,        // On garde les infos de l'entête (lot_id, num_facture, date, etc.)
                    ...item,         // On ajoute les infos de l'article (nom_matiere_snap, qte, etc.)
                    item_id_unique: item.id // Pour éviter les conflits de clés
                });
            });
        }
    });

    // 2. On applique les filtres sur cette liste plate
    return toutesLesLignes.filter(a => {
        // Gestion de la date (on privilégie la date de l'achat parent)
        const dateAchat = a.date_achat || a.created_at;
        let dateAchatStr = dateAchat ? new Date(dateAchat).toISOString().split('T')[0] : '';

        // Filtre par période (Date début / Date fin)
        const matchRange = (!dateRange.start || dateAchatStr >= dateRange.start) && 
                           (!dateRange.end || dateAchatStr <= dateRange.end);
        
        if (!matchRange) return false;

        // Filtres par colonnes
        return (
            (!colFilters.date_achat || dateAchatStr.includes(colFilters.date_achat)) &&
            String(a.id || '').toLowerCase().includes(colFilters.id.toLowerCase()) &&
            (a.lot_id || '').toLowerCase().includes(colFilters.lot_id.toLowerCase()) &&
            (a.num_facture || '').toLowerCase().includes(colFilters.num_facture.toLowerCase()) &&
            (a.nom_matiere_snap || '').toLowerCase().includes(colFilters.nom_matiere_snap.toLowerCase()) &&
            (a.nom_fournisseur_snap || '').toLowerCase().includes(colFilters.nom_fournisseur_snap.toLowerCase())
        );
    });
}, [achatsFiltresParOnglet, dateRange, colFilters]);
const groupedByLot = useMemo(() => {
    const groups = {};
    const baseData = achatsFiltresParOnglet.filter(a => {
        // 🔥 Correction : Utilise bien 'date_achat' renvoyé par le JOIN SQL
        const d = a.date_achat ? new Date(a.date_achat).toISOString().split('T')[0] : '';
        return (!dateRange.start || d >= dateRange.start) && (!dateRange.end || d <= dateRange.end);
    });
        baseData.forEach(a => {
            const lotKey = a.lot_id || "SANS-LOT";
            if (!groups[lotKey]) {
                groups[lotKey] = { 
                    lot_id: lotKey, date_lot: a.date_achat, qte_totale: 0, 
                    mt_total_cmp: 0, mt_total_facture: 0, 
                    utilisateur: a.nom_utilisateur || a.user_id || 'Admin',
                    is_active: a.is_active, is_archived: a.is_archived
                };
            }
            groups[lotKey].qte_totale += Number(a.qte_achetee || 0);
            groups[lotKey].mt_total_cmp += Number(a.montant_cmp_ancien || 0);
            groups[lotKey].mt_total_facture += Number(a.montant_facture_ligne || 0);
        });
        return Object.values(groups);
    }, [achatsFiltresParOnglet, dateRange]);

    const filterByLot = (lotId) => {
        setShowFullHistory(true);
        setColFilters({ ...initialFilters, lot_id: lotId === "SANS-LOT" ? "" : lotId });
    };

   const resetAllFilters = () => {
    setColFilters(initialFilters);
    setDateRange(initialDates);
    setShowFullHistory(false); // Cache le registre détaillé
    setActiveTab('actif');      // Revient sur l'onglet actif
};

    const handleFilterChange = (col, value) => {
        setColFilters(prev => ({ ...prev, [col]: value }));
    };

    const formatDateSafe = (dateStr) => {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
    };

    const formatTimeSafe = (dateStr) => {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const handleExportPDF = async () => {
        const input = document.getElementById('registre-mp-table');
        if (!input) return;
        try {
            const canvas = await html2canvas(input, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('l', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Historique_MP_${activeTab}.pdf`);
        } catch (error) { console.error(error); }
    };

    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredAchatsDetail);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Approvisionnements");
        XLSX.writeFile(workbook, `Historique_MP_${activeTab}.xlsx`);
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <ConfirmationModal 
                    isOpen={modalConfig.isOpen}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    confirmColor={modalConfig.confirmColor}
                    isAlert={modalConfig.isAlert}
                    onConfirm={handleConfirmAction}
                    onCancel={() => setModalConfig({ ...modalConfig, isOpen: false })}
                />
                
                <header style={headerBarStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={iconBox}><Leaf size={24} color="#fff" /></div>
                        <div>
                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                <h1 style={titleStyle}>HISTORIQUE APPROS MATIÈRES PREMIÈRES</h1>
                                {isInventoryActive && (
                                    <span style={lockBadge}><Lock size={10} /> INVENTAIRE EN COURS</span>
                                )}
                            </div>
                            <div style={dateBox}>
                                <Calendar size={14} color="#000" />
                                <input type="date" style={dateInput} value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} />
                                <span style={{fontWeight:'700', color:'#000'}}>au</span>
                                <input type="date" style={dateInput} value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} />
                            </div>
                        </div>
                    </div>
    
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={handleExportExcel} style={btnExcel} className="btn-effect"><Download size={16} /> Excel</button>
                        <button onClick={handleExportPDF} style={btnPdf} className="btn-effect"><FileDown size={16} /> PDF</button>
                        <button onClick={fetchData} style={{...btnRefresh, borderColor: isInventoryActive ? '#dc2626' : '#166534'}} disabled={loading} className="btn-effect">
                            <RefreshCcw size={16} color={isInventoryActive ? '#dc2626' : '#166534'} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </header>

                <div style={contentArea}>
                    {/* Indicateur de chargement discret */}
                    {loading && (
                        <div style={{ color: '#166534', fontWeight: '800', fontSize: '12px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <RefreshCcw size={14} className="animate-spin" /> SYNCHRONISATION...
                        </div>
                    )}

                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '12px'}}>
                        <div style={tabContainer}>
                            <button onClick={() => {setActiveTab('actif'); setShowFullHistory(false);}} style={activeTab === 'actif' ? activeTabStyle : inactiveTabStyle}>
                                <Inbox size={16} /> ACTIFS <span style={badgeCount}>{counts.actif}</span>
                            </button>
                            <button onClick={() => {setActiveTab('archives'); setShowFullHistory(false);}} style={activeTab === 'archives' ? activeArchiveStyle : inactiveTabStyle}>
                                <Archive size={16} /> ARCHIVES <span style={badgeCount}>{counts.archives}</span>
                            </button>
                            <button onClick={() => {setActiveTab('supprimes'); setShowFullHistory(false);}} style={activeTab === 'supprimes' ? activeDeleteTabStyle : inactiveTabStyle}>
                                <Trash2 size={16} /> SUPPRIMÉS <span style={{...badgeCount, background: '#fee2e2', color: '#dc2626'}}>{counts.supprimes}</span>
                            </button>
                        </div>

                        <div style={{display: 'flex', gap: '10px'}}>
                            <button onClick={() => setShowFullHistory(true)} style={btnShowAll} className="btn-effect"><Eye size={14} /> AFFICHER TOUT</button>
                            <button onClick={resetAllFilters} style={btnReset} className="btn-effect"><ListFilter size={14} /> RESET FILTRES</button>
                        </div>
                    </div>

                    <h3 style={sectionTitle}>RÉSUMÉ PAR LOT ({activeTab.toUpperCase()})</h3>
                    <div style={{...cardStyle, maxHeight: '250px', overflowY: 'auto', marginBottom: '20px'}}>
                        <table style={mainTable}>
                            <thead style={stickyHeader}>
                                <tr style={{background: '#f0fdf4', color: '#166534'}}><th style={thLotColor}>LOT ID</th><th style={thCenterLotColor}>QTE TOTAL</th><th style={thCenterLotColor}>MT TOTAL CMP</th><th style={thCenterLotColor}>MT FACTURE</th><th style={thCenterLotColor}>ECART TOTAL</th><th style={thLotColor}>UTILISATEUR</th><th style={thLotColor}>DATE & HEURE</th><th style={thCenterLotColor}>ÉTAT</th><th style={thCenterLotColor}>ACTION LOT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedByLot.map((lot) => {
                                    const ecart = lot.mt_total_facture - lot.mt_total_cmp;
                                    const isFrozen = isRowFrozen(lot.date_lot);
                                    const isDeleted = lot.is_active === 0;

                                    return (
                                        <tr key={lot.lot_id} style={trStyle}>
                                            <td style={tdStyle}>
                                                <span style={{...lotBadge, cursor: 'pointer', color: '#000'}} onClick={() => filterByLot(lot.lot_id)}>
                                                    {lot.lot_id}
                                                </span>
                                            </td>
                                            <td style={{...tdCenter, fontWeight: '700', color: '#000'}}>{lot.qte_totale}</td>
                                            <td style={{...tdCenter, fontWeight: '700', color: '#000'}}>{Math.round(lot.mt_total_cmp).toLocaleString()}</td>
                                            <td style={{...tdCenter, fontWeight: '700', color: '#000'}}>{Math.round(lot.mt_total_facture).toLocaleString()}</td>
                                            <td style={{...tdCenter, color: ecart >= 0 ? '#b91c1c' : '#15803d', fontWeight: '900'}}>
                                                {ecart > 0 ? '+' : ''}{Math.round(ecart).toLocaleString()}
                                            </td>
                                            <td style={{...tdStyle, fontWeight: '600', color: '#000'}}>{lot.utilisateur}</td>
                                            <td style={{...tdStyle, fontSize: '11px', fontWeight: '700', color: '#000'}}>
                                                {formatDateSafe(lot.date_lot)} {formatTimeSafe(lot.date_lot)}
                                            </td>
                                            <td style={tdCenter}>
                                                {isDeleted ? <span style={{color:'#dc2626', fontWeight:'800'}}>ANNULÉ</span> : 
                                                 isFrozen ? <span title="Clôturé" style={{color:'#15803d'}}><Snowflake size={16} /></span> : 
                                                 lot.is_archived === 1 ? <span style={{color:'#64748b', fontWeight:'700'}}>ARCHIVÉ</span> :
                                                 <span title="Modifiable" style={{color:'#22c55e'}}>● ACTIF</span>}
                                            </td>
                                            <td style={tdCenter}>
                                                <div style={{display:'flex', gap:'4px', justifyContent:'center'}}>
                                                    {activeTab === 'actif' && !isFrozen && !isInventoryActive && (
                                                        <>
                                                            <button onClick={() => openConfirm(lot.lot_id, 'ARCHIVE')} style={btnSmall} className="btn-effect" title="Archiver lot">
                                                                <Archive size={12} color="#166534" />
                                                            </button>
                                                            <button onClick={() => openConfirm(lot.lot_id, 'DELETE')} style={{...btnSmall, borderColor: '#ef4444', color: '#ef4444'}} className="btn-effect" title="Supprimer lot complet">
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </>
                                                    )}
                                                    {activeTab === 'archives' && !isFrozen && !isInventoryActive && (
                                                        <button onClick={() => openConfirm(lot.lot_id, 'UNARCHIVE')} style={btnSmall} className="btn-effect" title="Désarchiver">
                                                            <RefreshCcw size={12} color="#166534" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h3 style={sectionTitle}>REGISTRE DÉTAILLÉ MP (Lignes d'articles)</h3>
                    <div id="registre-mp-table" style={{...cardStyle, maxHeight: '400px', overflowY: 'auto'}}>
                        <table style={mainTable}>
              <thead style={stickyHeader}>
    <tr style={{background: '#f0fdf4', color: '#166534'}}>
        <th style={thLotColor}>ID ACHAT</th>
        <th style={thLotColor}>LOT ID</th>
        <th style={thLotColor}>N° FACTURE</th>
        <th style={thLotColor}>NOM MATIÈRE</th>
        <th style={thCenterLotColor}>QTE ACH.</th>
        
        <th style={thCenterLotColor}>MT HT</th>
        <th style={thCenterLotColor}>MT TVA</th>
        <th style={thCenterLotColor}>MT FAC. (TTC)</th>
        <th style={thCenterLotColor}>MT TOTAL CMP</th>
        <th style={thCenterLotColor}>ECART</th>
        <th style={thLotColor}>FOURNISSEUR</th>
        <th style={thLotColor}>DATE</th>
        <th style={thCenterLotColor}>ACTION LIGNE</th>
    </tr>
    <tr style={{ background: '#fff' }}>
        <th style={filterTh}><input placeholder="ID" style={filterInput} value={colFilters.id} onChange={(e) => handleFilterChange('id', e.target.value)} /></th>
        <th style={filterTh}><input placeholder="Lot" style={filterInput} value={colFilters.lot_id} onChange={(e) => handleFilterChange('lot_id', e.target.value)} /></th>
        <th style={filterTh}><input placeholder="Facture" style={filterInput} value={colFilters.num_facture} onChange={(e) => handleFilterChange('num_facture', e.target.value)} /></th>
        <th style={filterTh}><input placeholder="Matière" style={filterInput} value={colFilters.nom_matiere_snap} onChange={(e) => handleFilterChange('nom_matiere_snap', e.target.value)} /></th>
           <th colSpan={8} style={filterTh}></th>
        <th style={filterTh}></th>
    </tr>
</thead>
                          <tbody>
    {!showFullHistory && filteredAchatsDetail.length === 0 ? (
        /* 🔥 Ajustement du colSpan de 11 à 13 ici aussi */
        <tr><td colSpan="13" style={emptyState}>Sélectionnez un lot ci-dessus ou cliquez sur "Afficher tout"</td></tr>
    ) : (
        filteredAchatsDetail.map((achat, index) => {
            const ecart = Number(achat.montant_facture_ligne || 0) - Number(achat.montant_cmp_ancien || 0);
            const isFrozenLine = isRowFrozen(achat.date_achat);
            return (
                <tr key={achat.id || index} style={trStyle}>
                    <td style={{...tdStyle, fontWeight:'600'}}>{achat.id_achat || `ACH-${achat.id}`}</td>
                    <td style={tdStyle}><span style={lotBadge}>{achat.lot_id}</span></td>
                    <td style={{...tdStyle, fontWeight:'700'}}>{achat.num_facture}</td>
                    <td style={{...tdStyle, fontWeight: '800'}}>{achat.nom_matiere_snap}</td>
                    <td style={{...tdCenter, fontWeight: '700'}}>{achat.qte_achetee}</td>
                    <td style={{...tdCenter, color: '#475569', fontSize: '11px'}}>
                        {Math.round(Number(achat.montant_ht_ligne || 0)).toLocaleString()}
                    </td>
                    <td style={{...tdCenter, color: '#475569', fontSize: '11px'}}>
                        {Math.round(Number(achat.montant_tva_ligne || 0)).toLocaleString()}
                    </td>
                    <td style={{...tdCenter, fontWeight:'900'}}>{Math.round(Number(achat.montant_facture_ligne || 0)).toLocaleString()}</td>
                    
                    
                   

                    <td style={{...tdCenter}}>{Math.round(Number(achat.montant_cmp_ancien || 0)).toLocaleString()}</td>
                    <td style={{...tdCenter, color: ecart >= 0 ? '#b91c1c' : '#15803d', fontWeight: '900'}}>{ecart > 0 ? '+' : ''}{Math.round(ecart).toLocaleString()}</td>
                    <td style={{...tdStyle, fontWeight: '600'}}>{achat.nom_fournisseur_snap}</td>
                    <td style={{...tdStyle, fontSize: '10px', fontWeight: '700'}}>
                        {formatDateSafe(achat.date_achat)}
                    </td>
                    <td style={tdCenter}>
                        {activeTab === 'actif' && !isFrozenLine && !isInventoryActive && (
                            <button 
                                onClick={() => openConfirm(achat.id, 'DELETE_SINGLE')} 
                                style={{...btnSmall, borderColor: '#ef4444', color: '#ef4444'}} 
                                className="btn-effect"
                                title="Supprimer la ligne"
                            >
                                <Trash2 size={12} /> Ligne
                            </button>
                        )}
                    </td>
                </tr>
            );
        })
    )}
</tbody>
                        </table>
                    </div>
                </div>
            </main>
            <style>{`
                .btn-effect { transition: transform 0.1s ease; }
                .btn-effect:active { transform: scale(0.92); }
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

// --- STYLES (Conservés et complétés) ---
const badgeCount = { background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '900', marginLeft: '8px', border: '1px solid currentColor' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(2px)' };
const modalContent = { background: '#fff', padding: '24px', borderRadius: '12px', width: '420px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', border: '2px solid #166534' };
const btnCloseModal = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const btnCancelModal = { padding: '10px 18px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#fff', fontWeight: '700', cursor: 'pointer', fontSize: '13px' };
const btnConfirmModal = { padding: '10px 18px', borderRadius: '8px', border: 'none', color: '#fff', fontWeight: '700', cursor: 'pointer', fontSize: '13px' };
const tabContainer = { display: 'flex', gap: '5px', background: '#f0fdf4', padding: '4px', borderRadius: '10px', border: '2px solid #166534' };
const inactiveTabStyle = { padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b' };
const activeTabStyle = { ...inactiveTabStyle, background: '#fff', borderRadius: '6px', color: '#166534', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' };
const activeArchiveStyle = { ...activeTabStyle, color: '#166534' };
const activeDeleteTabStyle = { ...activeTabStyle, color: '#dc2626' }; 
const lockBadge = { background:'#fef2f2', color:'#dc2626', padding:'2px 8px', borderRadius:'4px', fontSize:'10px', fontWeight:'900', border:'1px solid #dc2626', display:'flex', alignItems:'center', gap:'4px' };
const stickyHeader = { position: 'sticky', top: 0, zIndex: 10 };
const emptyState = { textAlign: 'center', padding: '40px', color: '#64748b', fontStyle: 'italic', fontSize: '14px', fontWeight: '700' };
const layoutStyle = { display: 'flex', height: '100vh', background: '#F8FAFC' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const headerBarStyle = { background: '#fff', padding: '16px 24px', borderBottom: '3px solid #166534', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const iconBox = { background: '#166534', padding: '8px', borderRadius: '8px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#000' };
const dateBox = { display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', padding: '6px 12px', borderRadius: '8px', border: '2px solid #166534', marginTop: '6px' };
const dateInput = { border: 'none', background: 'transparent', fontSize: '13px', outline: 'none', fontWeight: '800', color: '#000' };
const contentArea = { padding: '20px', overflowY: 'auto' };
const sectionTitle = { fontSize: '13px', fontWeight: '900', color: '#166534', textTransform: 'uppercase', marginBottom: '8px' };
const cardStyle = { background: '#fff', borderRadius: '10px', border: '2px solid #b7e4c7', overflow: 'hidden' };
const mainTable = { width: '100%', borderCollapse: 'collapse', minWidth: '1300px' };
const thLotColor = { padding: '12px 10px', background: '#f0fdf4', color: '#166534', fontSize: '11px', fontWeight: '900', textAlign: 'left', borderBottom: '2px solid #166534' };
const thCenterLotColor = { ...thLotColor, textAlign: 'center' };
const tdStyle = { padding: '10px 10px', fontSize: '12px', color: '#000', whiteSpace: 'nowrap' }; 
const tdCenter = { ...tdStyle, textAlign: 'center' };
const trStyle = { borderBottom: '1px solid #e2e8f0' };
const filterTh = { padding: '8px 10px', borderBottom: '1px solid #166534', background: '#fff' };
const filterInput = { width: '100%', padding: '6px', fontSize: '11px', borderRadius: '6px', border: '2px solid #166534', outline: 'none', fontWeight: '700', color: '#000' };
const btnRefresh = { background: '#fff', border: '2px solid #166534', padding: '8px', borderRadius: '8px', cursor: 'pointer' };
const btnExcel = { background: '#15803d', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '900', display:'flex', alignItems:'center', gap:'8px' };
const btnPdf = { ...btnExcel, background: '#dc2626' };
const btnSmall = { border: '1.5px solid #166534', background: '#fff', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#166534', fontSize: '10px', fontWeight: '900' };
const lotBadge = { background: '#f0fdf4', color: '#000', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '900', border: '1.5px solid #166534' };
const btnShowAll = { background: '#166534', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '6px' };
const btnReset = { background: '#fff', color: '#166534', border: '2px solid #166534', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '6px' };

export default HistoriqueMp;